import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, Button, Badge, Select, Input } from '../components/ui';
import { CashFlowChart, AreaCard, DualLineCard } from '../components/Chart';
import { calendarDate } from '../lib/calendarDate';
import { api, ApiError } from '../lib/api';
import type { ApiResponse } from '../lib/types';

const now = new Date();
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const n = (v: unknown) => Number(v) || 0;
const usd = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

interface AwsSummary {
  revenue: number; invoice_count: number; invoice_paid_total: number; invoice_paid_count: number;
  aws: { id: number | null; total_cost: number; status: string; paid_at: string | null; last_synced_at: string | null };
  llm?: { total_cost: number; calls: number; estimated_calls: number; input_tokens: number; output_tokens: number };
  total_cost?: number;
  gross_margin: number; gross_margin_pct: number;
  tax: { de_rate: number; fed_rate: number; de_tax: number; fed_tax: number; total_tax: number; effective_rate: number };
  net: number; net_margin_pct: number;
}
interface LlmUsage {
  total_cost: number; calls: number; estimated_calls: number;
  tokens: { input: number; output: number; cache_read: number; cache_write: number };
  daily: { date: string; amount: number }[];
  by_feature: { feature: string; amount: number; calls: number }[];
  by_model: { model: string; amount: number; calls: number; input_tokens: number; output_tokens: number }[];
}
interface AwsData { summary: AwsSummary; daily_costs: { date: string; amount: number }[]; aws_bill_id: number | null; last_synced_at: string | null; ce_error?: string | null; llm?: LlmUsage | null; llm_bill?: Bill | null }

const FEATURE_LABEL: Record<string, string> = {
  fraud_analysis: 'Fraud Guard',
  feed_mapping: 'Feed Mapping (AI)',
  test_connection: 'Test Connection',
};
const tok = (v: number) => (v >= 1e6 ? `${(v / 1e6).toFixed(2)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}k` : String(v));

interface CashRow { month: number; invoice_paid: number; aws_paid: number; llm_paid?: number; net_cash: number; invoice_issued: number; aws_incurred: number; llm_incurred?: number }
interface Bill { id: number; total_cost: number; status: string; paid_at: string | null; last_synced_at: string | null }

interface ClosingRow { client_id: number; client_name: string; pageviews: number; clicks: number; cost: number }
interface Invoice { id: number; invoice_id: string; client_name?: string; pageviews?: number; clicks?: number; cost?: number; invoice_value?: number; status?: string; due_date?: string; view_hash?: string }

type Tab = 'closing' | 'invoices' | 'aws' | 'cashflow';

export default function Finance() {
  const [tab, setTab] = useState<Tab>('closing');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const [closing, setClosing] = useState<ClosingRow[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [aws, setAws] = useState<AwsData | null>(null);
  const [deRate, setDeRate] = useState('8.7');
  const [fedRate, setFedRate] = useState('21');
  const [awsBusy, setAwsBusy] = useState<string | null>(null);
  const [cashflow, setCashflow] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true); setError(null);
    const load = async () => {
      try {
        if (tab === 'closing') setClosing((await api.get<ApiResponse<ClosingRow[]>>(`/finance/closing?month=${month}&year=${year}`)).data || []);
        else if (tab === 'invoices') await loadInvoices();
        else if (tab === 'aws') await loadAws(false);
        else if (tab === 'cashflow') setCashflow((await api.get<ApiResponse<unknown>>(`/finance/cash-flow?year=${year}`)).data ?? null);
      } catch (e) { setError(e instanceof ApiError ? e.message : 'Failed'); }
      finally { setLoading(false); }
    };
    load();
  }, [tab, month, year]);

  async function loadInvoices() {
    setInvoices((await api.get<ApiResponse<Invoice[]>>(`/finance/invoices?month=${month}&year=${year}`)).data || []);
  }

  async function loadAws(refresh: boolean) {
    const de = (parseFloat(deRate) || 0) / 100;
    const fed = (parseFloat(fedRate) || 0) / 100;
    const qs = `month=${month}&year=${year}&deRate=${de}&fedRate=${fed}${refresh ? '&refresh=1' : ''}`;
    if (refresh) setAwsBusy('refresh');
    try {
      const r = await api.get<ApiResponse<AwsData>>(`/finance/aws-summary?${qs}`);
      setAws(r.data || null);
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Failed'); }
    finally { if (refresh) setAwsBusy(null); }
  }

  async function syncLlm() {
    setAwsBusy('llm-sync');
    try {
      const r = await api.post<{ imported: number; skipped: number; ratio_used: number; from_observed: boolean }>(
        `/finance/llm-sync?month=${month}&year=${year}`, {}
      );
      await loadAws(false);
      const basis = r.from_observed ? 'measured from real calls' : 'default assumption';
      alert(`LLM costs synced.
${r.imported} imported, ${r.skipped} skipped.
Input/output split: ${(r.ratio_used * 100).toFixed(0)}% input (${basis}).`);
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed to sync LLM costs'); }
    finally { setAwsBusy(null); }
  }

  async function toggleLlmPaid(billId: number, paid: boolean) {
    setAwsBusy('llm-paid');
    try {
      await api.patch(`/finance/llm-bills/${billId}/${paid ? 'paid' : 'unpaid'}`);
      await loadAws(false);
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed'); }
    finally { setAwsBusy(null); }
  }

  async function toggleAwsPaid(billId: number, paid: boolean) {
    setAwsBusy('paid');
    try {
      await api.patch(`/finance/aws-bills/${billId}/${paid ? 'paid' : 'unpaid'}`);
      await loadAws(false);
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed'); }
    finally { setAwsBusy(null); }
  }

  // Valor editado por cliente antes de gerar. Fica separado de `closing`
  // para nao perder o que foi digitado quando a lista recarregar.
  const [editValue, setEditValue] = useState<Record<number, string>>({});

  async function setPaid(iv: Invoice, paid: boolean) {
    setBusy(iv.id);
    try {
      await api.patch(`/finance/invoices/${iv.id}/${paid ? 'paid' : 'unpaid'}`);
      loadInvoices();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed'); } finally { setBusy(null); }
  }

  async function backToClosing(iv: Invoice) {
    if (!confirm(`Send ${iv.invoice_id} back to Closing? The invoice is deleted and the client returns to the Closing tab.`)) return;
    setBusy(iv.id);
    try {
      await api.del(`/finance/invoices/${iv.id}`);
      loadInvoices();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed'); } finally { setBusy(null); }
  }

  async function generateInvoice(r: ClosingRow) {
    const raw = editValue[r.client_id];
    const value = raw !== undefined && raw !== '' ? Number(raw) : r.cost;
    if (!Number.isFinite(value) || value < 0) { alert('Invoice value must be a number >= 0'); return; }
    setBusy(r.client_id);
    try {
      await api.post('/finance/invoice', { client_id: r.client_id, period_month: month, period_year: year, pageviews: r.pageviews, clicks: r.clicks, cost: r.cost, invoice_value: value });
      setClosing((c) => c.filter((x) => x.client_id !== r.client_id));
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed'); } finally { setBusy(null); }
  }

  const TABS: { v: Tab; l: string }[] = [{ v: 'closing', l: 'Closing' }, { v: 'invoices', l: 'Invoices' }, { v: 'aws', l: 'AWS P&L' }, { v: 'cashflow', l: 'Cash Flow' }];

  return (
    <Layout
      title="Finance"
      actions={
        <div className="flex gap-2">
          {tab !== 'cashflow' && (
            <Select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="w-24">{MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</Select>
          )}
          <Select value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24">{[year, year - 1, year - 2].map((y) => <option key={y} value={y}>{y}</option>)}</Select>
        </div>
      }
    >
      <div className="flex gap-1 mb-5">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === t.v ? 'bg-chrome-800 text-white' : 'bg-surface-muted text-slate-600 hover:bg-slate-200'}`}>{t.l}</button>
        ))}
      </div>

      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div>}
      {loading ? (
        <Card className="p-10 text-center text-slate-400">Loading…</Card>
      ) : tab === 'closing' ? (
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">Clients to close ({MONTHS[month - 1]}/{year})</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-500 bg-surface-muted"><th className="px-4 py-2.5 font-medium">Client</th><th className="px-4 py-2.5 font-medium text-right">Pageviews</th><th className="px-4 py-2.5 font-medium text-right">Clicks</th><th className="px-4 py-2.5 font-medium text-right">Cost</th><th className="px-4 py-2.5 font-medium text-right">Invoice value</th><th className="px-4 py-2.5 text-right"></th></tr></thead>
            <tbody className="divide-y divide-border">
              {closing.map((r) => (
                <tr key={r.client_id}>
                  <td className="px-4 py-2.5 font-medium text-ink-900">{r.client_name}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700">{n(r.pageviews).toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700">{n(r.clicks).toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700">${n(r.cost).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-28 text-right"
                      value={editValue[r.client_id] ?? n(r.cost).toFixed(2)}
                      onChange={(e) => setEditValue((v) => ({ ...v, [r.client_id]: e.target.value }))}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-right"><Button disabled={busy === r.client_id} onClick={() => generateInvoice(r)}>{busy === r.client_id ? '…' : 'Generate Invoice'}</Button></td>
                </tr>
              ))}
              {closing.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Nothing to close.</td></tr>}
            </tbody>
          </table>
        </Card>
      ) : tab === 'invoices' ? (
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">Invoices ({MONTHS[month - 1]}/{year})</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-500 bg-surface-muted"><th className="px-4 py-2.5 font-medium">Invoice</th><th className="px-4 py-2.5 font-medium">Client</th><th className="px-4 py-2.5 font-medium text-right">Cost</th><th className="px-4 py-2.5 font-medium text-right">Value</th><th className="px-4 py-2.5 font-medium">Status</th><th className="px-4 py-2.5 font-medium">Due</th><th className="px-4 py-2.5"></th></tr></thead>
            <tbody className="divide-y divide-border">
              {invoices.map((iv) => (
                <tr key={iv.invoice_id}>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{iv.invoice_id}</td>
                  <td className="px-4 py-2.5 text-slate-700">{iv.client_name || '—'}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700">${n(iv.cost).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700">${n(iv.invoice_value).toFixed(2)}</td>
                  <td className="px-4 py-2.5"><Badge tone={iv.status === 'paid' ? 'green' : iv.status === 'overdue' ? 'red' : 'amber'}>{iv.status || 'pending'}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-500">{iv.due_date ? calendarDate(iv.due_date) : '—'}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      {iv.view_hash && <a className="text-brand-600 underline text-xs mr-1" href={`/_api/invoice/view/${iv.view_hash}`} target="_blank" rel="noreferrer">View</a>}
                      {iv.status === 'paid' ? (
                        <Button variant="outline" disabled={busy === iv.id} onClick={() => setPaid(iv, false)}>Unmark paid</Button>
                      ) : (
                        <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy === iv.id} onClick={() => setPaid(iv, true)}>✓ Mark paid</Button>
                      )}
                      {/* Invoice paga nao volta pro Closing: apagar um recebimento
                          desbalancearia o Cash Flow, que soma por paid_at. */}
                      {iv.status !== 'paid' && (
                        <Button variant="ghost" disabled={busy === iv.id} onClick={() => backToClosing(iv)}>↩ Back to Closing</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No invoices found.</td></tr>}
            </tbody>
          </table>
        </Card>
      ) : tab === 'aws' ? (
        (() => {
          const sm = aws?.summary;
          const daily = (aws?.daily_costs || []).map((d) => ({ x: String(Number(d.date.slice(8, 10))), y: n(d.amount) }));
          const llm = aws?.llm || null;
          // Custo diário AWS + LLM no mesmo eixo de dias do mês.
          const llmByDay = new Map((llm?.daily || []).map((d) => [Number(d.date.slice(8, 10)), n(d.amount)]));
          const awsByDay = new Map((aws?.daily_costs || []).map((d) => [Number(d.date.slice(8, 10)), n(d.amount)]));
          const days = [...new Set([...awsByDay.keys(), ...llmByDay.keys()])].sort((a, b) => a - b);
          const dailyCombined = days.map((d) => ({ x: String(d), a: awsByDay.get(d) || 0, b: llmByDay.get(d) || 0 }));
          const hasLlm = !!llm && (n(llm.total_cost) > 0 || n(llm.calls) > 0);
          const pct = (v: number) => `${n(v).toFixed(2)}%`;
          const KPI = ({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone: string }) => (
            <div className={`rounded-xl border-2 bg-surface p-4 ${tone}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
              <div className="text-2xl font-bold text-ink-900 mt-1">{value}</div>
              {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
            </div>
          );
          return (
            <>
              {/* Controles do P&L */}
              <Card className="p-4 mb-5">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="text-sm font-semibold text-ink-900 mr-2">P&amp;L — Revenue vs Cost <span className="font-normal text-slate-400">(AWS + LLM, with Delaware + Federal tax)</span></div>
                  <label className="text-xs text-slate-500">DE %:<Input value={deRate} onChange={(e) => setDeRate(e.target.value)} className="w-20 mt-0.5" /></label>
                  <label className="text-xs text-slate-500">Federal %:<Input value={fedRate} onChange={(e) => setFedRate(e.target.value)} className="w-20 mt-0.5" /></label>
                  <Button onClick={() => loadAws(false)}>Load</Button>
                  <Button variant="outline" onClick={() => loadAws(true)} disabled={awsBusy === 'refresh'}>{awsBusy === 'refresh' ? '⏳ Syncing…' : '↻ Refresh AWS'}</Button>
                  <Button variant="outline" onClick={syncLlm} disabled={awsBusy === 'llm-sync'} title="Import cost of past Fraud Guard analyses into the P&L">{awsBusy === 'llm-sync' ? '⏳ Syncing…' : '↻ Sync LLM'}</Button>
                  <span className="text-xs text-slate-400">AWS synced: {aws?.last_synced_at ? new Date(aws.last_synced_at).toLocaleString() : '—'}</span>
                </div>
                {aws?.ce_error && <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2">Cost Explorer: {aws.ce_error}</div>}
              </Card>

              {!sm ? <Card className="p-10 text-center text-slate-400">No data for {MONTHS[month - 1]}/{year}.</Card> : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-5">
                    <KPI label="Revenue (invoices)" value={usd(n(sm.revenue))} sub={`${n(sm.invoice_count)} invoices`} tone="border-border" />
                    <KPI label="AWS Cost" value={usd(n(sm.aws?.total_cost))} sub={sm.aws?.status === 'paid' ? 'paid' : 'unpaid'} tone="border-border" />
                    <KPI label="LLM / AI" value={usd(n(sm.llm?.total_cost))} sub={`${n(sm.llm?.calls)} calls · ${aws?.llm_bill?.status === 'paid' ? 'paid' : 'unpaid'}`} tone="border-violet-300" />
                    <KPI label="Gross Margin" value={usd(n(sm.gross_margin))} sub={pct(sm.gross_margin_pct)} tone={n(sm.gross_margin) < 0 ? 'border-rose-300' : 'border-emerald-300'} />
                    <KPI label="Total Tax" value={usd(n(sm.tax?.total_tax))} sub={`eff ${pct(n(sm.tax?.effective_rate) * 100)}`} tone="border-amber-300" />
                    <KPI label="NET" value={usd(n(sm.net))} sub={pct(sm.net_margin_pct)} tone={n(sm.net) < 0 ? 'border-rose-300' : 'border-emerald-300'} />
                    <KPI label="Invoice Paid" value={usd(n(sm.invoice_paid_total))} sub={`${n(sm.invoice_paid_count)} of ${n(sm.invoice_count)}`} tone="border-sky-300" />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {hasLlm
                      ? <DualLineCard title="Daily Cost — AWS vs LLM" data={dailyCombined} aName="AWS" bName="LLM / AI" aColor="#29abe2" bColor="#8b5cf6" />
                      : <AreaCard title="AWS Daily Cost" data={daily} />}

                    <Card className="overflow-hidden">
                      <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">P&amp;L Breakdown</div>
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-border">
                          <tr><td className="px-4 py-2 text-slate-600">Revenue (sum invoice_value)</td><td className="px-4 py-2 text-right text-slate-700">{usd(n(sm.revenue))}</td></tr>
                          <tr><td className="px-4 py-2 text-slate-600">AWS Cost (UnblendedCost)</td><td className="px-4 py-2 text-right text-slate-700">- {usd(n(sm.aws?.total_cost))}</td></tr>
                          <tr>
                            <td className="px-4 py-2 text-slate-600">
                              LLM / AI
                              <span className="block text-[11px] text-slate-400">
                                {tok(n(sm.llm?.input_tokens))} in / {tok(n(sm.llm?.output_tokens))} out · billed outside AWS
                                {n(sm.llm?.estimated_calls) > 0 && ` · ${n(sm.llm?.estimated_calls)} estimated`}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right text-slate-700">- {usd(n(sm.llm?.total_cost))}</td>
                          </tr>
                          <tr className={n(sm.gross_margin) < 0 ? 'bg-rose-50' : 'bg-emerald-50'}>
                            <td className="px-4 py-2 font-semibold text-ink-900">Gross Margin ({pct(sm.gross_margin_pct)})</td>
                            <td className="px-4 py-2 text-right font-semibold text-ink-900">{usd(n(sm.gross_margin))}</td>
                          </tr>
                          <tr><td className="px-4 py-2 text-slate-600">Delaware tax ({pct(n(sm.tax?.de_rate) * 100)})</td><td className="px-4 py-2 text-right text-slate-700">- {usd(n(sm.tax?.de_tax))}</td></tr>
                          <tr><td className="px-4 py-2 text-slate-600">Federal tax ({pct(n(sm.tax?.fed_rate) * 100)}, after DE deduction)</td><td className="px-4 py-2 text-right text-slate-700">- {usd(n(sm.tax?.fed_tax))}</td></tr>
                          <tr className={n(sm.net) < 0 ? 'bg-rose-50' : 'bg-emerald-50'}>
                            <td className="px-4 py-2 font-bold text-ink-900">NET ({pct(sm.net_margin_pct)})</td>
                            <td className="px-4 py-2 text-right font-bold text-ink-900">{usd(n(sm.net))}</td>
                          </tr>
                        </tbody>
                      </table>
                      {aws?.llm_bill?.id && n(aws.llm_bill.total_cost) > 0 && (
                        <div className="p-4 border-t border-border">
                          {aws.llm_bill.status === 'paid' ? (
                            <div className="flex items-center gap-3">
                              <Badge tone="green">LLM bill paid{aws.llm_bill.paid_at ? ` on ${new Date(aws.llm_bill.paid_at).toLocaleDateString()}` : ''}</Badge>
                              <Button variant="outline" disabled={awsBusy === 'llm-paid'} onClick={() => toggleLlmPaid(aws.llm_bill!.id, false)}>Undo</Button>
                            </div>
                          ) : (
                            <Button disabled={awsBusy === 'llm-paid'} onClick={() => toggleLlmPaid(aws.llm_bill!.id, true)}>{awsBusy === 'llm-paid' ? '…' : 'Mark LLM Bill as Paid'}</Button>
                          )}
                        </div>
                      )}
                      {aws?.aws_bill_id && (
                        <div className="p-4 border-t border-border">
                          {sm.aws?.status === 'paid' ? (
                            <div className="flex items-center gap-3">
                              <Badge tone="green">AWS Bill paid{sm.aws.paid_at ? ` on ${new Date(sm.aws.paid_at).toLocaleDateString()}` : ''}</Badge>
                              <Button variant="outline" disabled={awsBusy === 'paid'} onClick={() => toggleAwsPaid(aws.aws_bill_id!, false)}>Undo</Button>
                            </div>
                          ) : (
                            <Button disabled={awsBusy === 'paid'} onClick={() => toggleAwsPaid(aws.aws_bill_id!, true)}>{awsBusy === 'paid' ? '…' : 'Mark AWS Bill as Paid'}</Button>
                          )}
                        </div>
                      )}
                    </Card>
                  </div>

                  {hasLlm && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
                      <Card className="overflow-hidden">
                        <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">LLM by feature</div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                              <th className="px-4 py-2.5 font-medium">Feature</th>
                              <th className="px-4 py-2.5 font-medium text-right">Calls</th>
                              <th className="px-4 py-2.5 font-medium text-right">Cost</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {(llm!.by_feature || []).map((f) => (
                              <tr key={f.feature}>
                                <td className="px-4 py-2 text-slate-600">{FEATURE_LABEL[f.feature] || f.feature}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{n(f.calls)}</td>
                                <td className="px-4 py-2 text-right text-slate-700">{usd(n(f.amount))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Card>

                      <Card className="overflow-hidden">
                        <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">LLM by model</div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                              <th className="px-4 py-2.5 font-medium">Model</th>
                              <th className="px-4 py-2.5 font-medium text-right">Tokens (in / out)</th>
                              <th className="px-4 py-2.5 font-medium text-right">Cost</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {(llm!.by_model || []).map((m) => (
                              <tr key={m.model}>
                                <td className="px-4 py-2 font-mono text-xs text-slate-600">{m.model}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{tok(n(m.input_tokens))} / {tok(n(m.output_tokens))}</td>
                                <td className="px-4 py-2 text-right text-slate-700">{usd(n(m.amount))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {n(llm!.estimated_calls) > 0 && (
                          <div className="px-4 py-2.5 border-t border-border text-[11px] text-amber-700 bg-amber-50">
                            {n(llm!.estimated_calls)} call{n(llm!.estimated_calls) === 1 ? '' : 's'} with a model outside the pricing table — estimated cost.
                          </div>
                        )}
                      </Card>
                    </div>
                  )}
                </>
              )}
            </>
          );
        })()
      ) : (
        (() => {
          const rows = (cashflow as CashRow[] | null) || [];
          const sum = (k: keyof CashRow) => rows.reduce((a, r) => a + n(r[k]), 0);
          const cashIn = sum('invoice_paid');
          const awsOut = sum('aws_paid'), llmOut = sum('llm_paid');
          const cashOut = awsOut + llmOut;
          const net = cashIn - cashOut;
          const issued = sum('invoice_issued');
          const incurred = sum('aws_incurred') + sum('llm_incurred');
          const receivables = issued - cashIn;
          const chartData = rows.map((r) => ({
            m: MONTHS_EN[n(r.month) - 1] || String(r.month),
            invoice_paid: n(r.invoice_paid), aws_paid: n(r.aws_paid), llm_paid: n(r.llm_paid),
            invoice_issued: n(r.invoice_issued), aws_incurred: n(r.aws_incurred) + n(r.llm_incurred), net_cash: n(r.net_cash),
          }));
          const kpi = (label: string, value: number, tone: string, sub?: string) => (
            <div className={`rounded-xl border p-4 ${tone}`}>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
              <div className="text-xl font-semibold mt-0.5">{usd(value)}</div>
              {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
            </div>
          );
          return (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
                {kpi('Cash In (paid)', cashIn, 'bg-emerald-50 border-emerald-200 text-emerald-800')}
                {kpi('Cash Out (paid)', cashOut, 'bg-rose-50 border-rose-200 text-rose-800', `AWS ${usd(awsOut)} + LLM ${usd(llmOut)}`)}
                {kpi('Net Cash', net, net >= 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800')}
                {kpi('Issued (accrued)', issued, 'bg-surface border-border text-slate-700')}
                {kpi('Incurred (accrued)', incurred, 'bg-surface border-border text-slate-700')}
                {kpi('Receivables Open', receivables, 'bg-amber-50 border-amber-200 text-amber-800', 'issued − paid')}
              </div>

              <Card className="p-5 mb-5">
                <div className="text-sm font-semibold text-ink-900 mb-1">Cash Flow {year} — Invoice Paid vs AWS + LLM Bills Paid</div>
                <p className="text-[11px] text-slate-400 mb-3">Bars = actual cash (paid). Thin lines = accrual (issued/incurred). Dark line = net cash.</p>
                <CashFlowChart data={chartData} />
              </Card>

              <Card className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500 bg-surface-muted">
                      <th className="px-4 py-2.5 font-medium">Month</th>
                      <th className="px-4 py-2.5 font-medium text-right">Invoice Paid (cash in)</th>
                      <th className="px-4 py-2.5 font-medium text-right">AWS Paid (cash out)</th>
                      <th className="px-4 py-2.5 font-medium text-right">LLM Paid (cash out)</th>
                      <th className="px-4 py-2.5 font-medium text-right">Net Cash</th>
                      <th className="px-4 py-2.5 font-medium text-right">Invoice Issued (accrued)</th>
                      <th className="px-4 py-2.5 font-medium text-right">AWS Incurred (accrued)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((r) => (
                      <tr key={r.month} className={n(r.net_cash) >= 0 ? 'bg-emerald-50/40' : 'bg-rose-50/40'}>
                        <td className="px-4 py-2 text-slate-600">{MONTHS_EN[n(r.month) - 1]}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{usd(n(r.invoice_paid))}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{usd(n(r.aws_paid))}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{usd(n(r.llm_paid))}</td>
                        <td className="px-4 py-2 text-right font-semibold text-ink-900">{usd(n(r.net_cash))}</td>
                        <td className="px-4 py-2 text-right text-slate-400">{usd(n(r.invoice_issued))}</td>
                        <td className="px-4 py-2 text-right text-slate-400">{usd(n(r.aws_incurred))}</td>
                      </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No data for {year}.</td></tr>}
                  </tbody>
                  {rows.length > 0 && (
                    <tfoot>
                      <tr className="bg-surface-muted font-semibold">
                        <td className="px-4 py-2.5">Total</td>
                        <td className="px-4 py-2.5 text-right">{usd(cashIn)}</td>
                        <td className="px-4 py-2.5 text-right">{usd(awsOut)}</td>
                        <td className="px-4 py-2.5 text-right">{usd(llmOut)}</td>
                        <td className="px-4 py-2.5 text-right">{usd(net)}</td>
                        <td className="px-4 py-2.5 text-right text-slate-500">{usd(issued)}</td>
                        <td className="px-4 py-2.5 text-right text-slate-500">{usd(incurred)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </Card>
            </>
          );
        })()
      )}
    </Layout>
  );
}
